import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Layout } from "@/components/layout/Layout";
import { AnimatedSection } from "@/components/ui/AnimatedSection";
import { PricingCard } from "@/components/ui/PricingCard";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const pricingPlans = [
  {
    name: "Starter",
    price: "$2,000",
    description: "Perfect for businesses starting their AI marketing journey.",
    features: [
      "1 core service focus",
      "Monthly strategy session",
      "Basic performance reporting",
      "Email support",
      "30-day optimization cycles",
    ],
  },
  {
    name: "Growth",
    price: "$5,000",
    description: "For businesses ready to scale with AI-powered marketing.",
    features: [
      "2-3 integrated services",
      "Weekly optimization calls",
      "Advanced analytics dashboard",
      "Priority Slack support",
      "Bi-weekly optimization cycles",
      "Custom AI workflows",
      "A/B testing included",
    ],
    popular: true,
  },
  {
    name: "Scale",
    price: "$10,000",
    description: "Full-service AI marketing for ambitious B2B leaders.",
    features: [
      "All 4 services included",
      "Dedicated strategist",
      "Daily performance monitoring",
      "24/7 priority support",
      "Weekly optimization cycles",
      "Custom AI agent builds",
      "Executive reporting",
      "Quarterly strategy reviews",
    ],
  },
];

const faqs = [
  {
    question: "What's included in each tier?",
    answer: "Each tier includes our core AI marketing services with varying levels of support and optimization frequency. Starter focuses on one service, Growth combines 2-3 services for integrated campaigns, and Scale provides full-service coverage with a dedicated strategist.",
  },
  {
    question: "What's the minimum contract length?",
    answer: "We work on 3-month minimum engagements. This gives us enough time to implement, optimize, and demonstrate measurable results. After the initial period, you can continue month-to-month.",
  },
  {
    question: "Can I upgrade or downgrade my plan?",
    answer: "Yes, you can change your plan at any time. We'll prorate the difference and adjust your services accordingly. Most clients upgrade as they see results.",
  },
  {
    question: "What industries do you work with?",
    answer: "We specialize in B2B companies including SaaS (Seed to Series B), IT services and dev agencies, AI tools and startups, and high-ticket clinics/practices in the US and Australia.",
  },
  {
    question: "How quickly will I see results?",
    answer: "Most clients see initial improvements within 30-60 days. Significant results typically appear by month 3, with compounding growth thereafter. We provide regular reporting so you can track progress.",
  },
  {
    question: "Do you offer one-time projects?",
    answer: "Yes! Our AI Agent Build service is available as a one-time project ($5,000-$15,000). This is perfect for businesses that want a custom AI chatbot or automation without a monthly retainer.",
  },
];

export default function Pricing() {
  return (
    <Layout>
      {/* Hero */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-secondary/30 to-background pointer-events-none" />
        <div className="absolute top-1/3 left-1/4 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl pointer-events-none" />
        
        <div className="container-wide relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl mx-auto text-center"
          >
            <h1 className="heading-hero mb-6">
              Simple, Transparent Pricing
            </h1>
            <p className="text-xl text-muted-foreground">
              Choose the plan that fits your growth stage. All plans include our 
              AI-powered marketing expertise.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="pb-24">
        <div className="container-wide">
          <div className="grid lg:grid-cols-3 gap-8 items-start">
            {pricingPlans.map((plan, index) => (
              <PricingCard key={plan.name} {...plan} index={index} />
            ))}
          </div>
        </div>
      </section>

      {/* AI Agent Build */}
      <section className="section-spacing bg-secondary/30">
        <div className="container-wide">
          <div className="max-w-4xl mx-auto">
            <AnimatedSection className="text-center mb-12">
              <span className="inline-block px-4 py-2 rounded-full bg-background text-sm font-medium mb-6">
                One-Time Offer
              </span>
              <h2 className="heading-section mb-6">AI Agent Build</h2>
              <p className="text-xl text-muted-foreground">
                Need a custom AI agent without a monthly retainer? 
                Get a fully-built AI solution for your business.
              </p>
            </AnimatedSection>

            <div className="grid md:grid-cols-3 gap-6">
              {[
                {
                  name: "Starter Agent",
                  price: "$5,000",
                  features: ["Single-purpose bot", "Basic integrations", "30-day support"],
                },
                {
                  name: "Pro Agent",
                  price: "$10,000",
                  features: ["Multi-channel bot", "CRM integration", "Custom training", "60-day support"],
                },
                {
                  name: "Enterprise Agent",
                  price: "$15,000",
                  features: ["Advanced AI model", "Full automation suite", "Ongoing training", "90-day support"],
                },
              ].map((agent, index) => (
                <AnimatedSection key={agent.name} delay={index * 0.1}>
                  <div className="p-6 rounded-2xl bg-background border border-border">
                    <h3 className="font-semibold mb-2">{agent.name}</h3>
                    <div className="text-3xl font-bold mb-4">{agent.price}</div>
                    <ul className="space-y-2 mb-6">
                      {agent.features.map((feature) => (
                        <li key={feature} className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Check className="w-4 h-4 text-foreground" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                    <Link to="/ai-agents">
                      <Button variant="outline" className="w-full rounded-full">
                        Learn More
                      </Button>
                    </Link>
                  </div>
                </AnimatedSection>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-spacing">
        <div className="container-narrow">
          <AnimatedSection className="text-center mb-16">
            <h2 className="heading-section mb-6">Frequently Asked Questions</h2>
          </AnimatedSection>

          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem
                key={index}
                value={`faq-${index}`}
                className="border border-border rounded-2xl px-6 data-[state=open]:bg-secondary/30"
              >
                <AccordionTrigger className="text-left text-lg font-medium py-6 hover:no-underline">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground pb-6">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="section-spacing bg-primary text-primary-foreground">
        <div className="container-narrow text-center">
          <AnimatedSection>
            <h2 className="heading-section mb-6">
              Ready to Get Started?
            </h2>
            <p className="text-xl text-primary-foreground/70 max-w-2xl mx-auto mb-10">
              Book a free strategy call to find the right plan for your business.
            </p>
            <Button
              size="lg"
              className="rounded-full px-10 text-base h-14 bg-primary-foreground text-primary hover:bg-primary-foreground/90"
              data-cal-link="thecloudseo/30-min-meeting"
              data-cal-namespace="30-min-meeting"
              data-cal-config='{"layout":"month_view"}'
            >
              Book a Strategy Call
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </AnimatedSection>
        </div>
      </section>
    </Layout>
  );
}
